/**
 * The rulebase manager's shared vocabulary (M4).
 *
 * One module owns the two questions every M4 surface asks: "is this Gemeinde a
 * draft?" and "how is its pack addressed?". Both answers were being derived in
 * three places before, and a Gemeinde that reads as verified on one surface and
 * as a draft on another is exactly the failure this milestone exists to prevent.
 */
import type { Translate } from '@/i18n'

import type { Citation, DraftParameter, DraftZone, GemeindePack, StatusCount } from '@/api/types'

/**
 * A pack is a DRAFT until a human has verified something in it against the BZO.
 *
 * The backend states this directly as `draft`. The same fact is also in `mode`,
 * which is `indicative` exactly while no zone of the pack is verified
 * (app/rulebase_index.py); the explicit flag wins, and the fallback covers a
 * response that predates it — derived from the backend's own data, never assumed.
 */
export function isDraft(pack: GemeindePack): boolean {
  const explicit: boolean | undefined = pack.draft
  if (typeof explicit === 'boolean') return explicit
  return pack.mode !== 'verified'
}

/** `zh/egg` — the pack's stable key, as the backend names it. */
export function packIdOf(pack: GemeindePack): string {
  return pack.pack_id ?? pack.pack
}

/**
 * `zh/egg` → `{ kanton: 'zh', gemeinde: 'egg' }`.
 *
 * The two parts become two URL segments rather than one encoded one: a `%2F`
 * inside a path segment survives neither every proxy nor every history API
 * implementation intact, and the pack key is already two levels by construction.
 */
export function splitPackId(packId: string): { kanton: string; gemeinde: string } {
  const [kanton = '', ...rest] = packId.split('/')
  return { kanton, gemeinde: rest.join('/') }
}

export function joinPackId(kanton: string, gemeinde: string): string {
  return `${kanton}/${gemeinde}`
}

/** Verified share as a percentage, 0 when nothing is countable. */
export function verifiedPercent(counts: StatusCount): number {
  const total = counts.total ?? 0
  if (total <= 0) return 0
  return Math.round((counts.verified / total) * 100)
}

/** Everything that is NOT verified — the number the reader actually needs. */
export function openCount(counts: StatusCount): number {
  return (counts.total ?? 0) - counts.verified
}

/**
 * Does this citation actually let a human check the claim?
 *
 * A page number without a snippet, or a snippet without a page, is not a
 * citation — it is a gesture at one. The confirm affordances are rendered only
 * where this returns true, which is why "confirm without a citation" is not a
 * state the UI can reach.
 */
export function isCheckable(citation: Citation | null | undefined): boolean {
  if (!citation) return false
  if (typeof citation.page !== 'number' || citation.page < 1) return false
  return typeof citation.snippet === 'string' && citation.snippet.trim() !== ''
}

/** The citation a parameter should be checked against: its own, else the row's. */
export function citationFor(
  zone: DraftZone,
  parameter: DraftParameter,
): Citation | null {
  const own = parameter.citation ?? null
  if (isCheckable(own)) return own
  const row = zone.citation ?? null
  return isCheckable(row) ? row : null
}

/** Parameters of a zone row that still need a human — the work in front of you. */
export function openParameters(zone: DraftZone): DraftParameter[] {
  return (zone.parameters ?? []).filter((parameter) => parameter.status !== 'verified')
}

/**
 * How a value is written on screen.
 *
 * `null` is the interesting case: an unread parameter renders as the "not read"
 * marker, never as `0`, never as an empty string that could be mistaken for one.
 * Numbers use de-CH grouping, as everywhere else in this product.
 */
export function formatValue(
  value: string | number | boolean | null | undefined,
  notRead: string,
  yes: string,
  no: string,
): string {
  if (value === null || value === undefined) return notRead
  if (typeof value === 'boolean') return value ? yes : no
  if (typeof value === 'number') return new Intl.NumberFormat('de-CH').format(value)
  return value
}

/**
 * The second line under a cell's value: how it is written, and what that means.
 *
 * Two cases, both from the backend — nothing is derived here, because the
 * notation of a Ziffer is a deterministic reading of the document and it is
 * made in one place (`app/draftpacks.py`), not a second time in the browser:
 *
 *  · a ratio-typed parameter (Ausnützungsziffer, Grünflächenziffer) shows the
 *    notation the BZO prints AND the normalised fraction, so the person
 *    checking the cell sees «100 %» on the page and `1.00` in the pack and can
 *    tell they are the same number. An undetermined notation says so and asks
 *    for the value as printed, rather than showing a converted number nobody
 *    can check;
 *  · a value read under an older BZO's own term shows that term, because the
 *    citation is checkable only against the word that stands on the page.
 */
export function entryNote(parameter: DraftParameter, t: Translate): string | null {
  const ratio = parameter.ratio ?? null
  if (ratio) {
    const printed = ratio.printed_de ?? ''
    if (ratio.notation === 'undetermined' || ratio.normalised === null) {
      return t('verify.ratioOpen', { printed })
    }
    if (ratio.notation === 'percent') {
      return t('verify.ratioBoth', {
        printed,
        normalised: new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2 }).format(
          ratio.normalised ?? 0,
        ),
      })
    }
  }
  if (parameter.read_as_de) return t('verify.readAs', { term: parameter.read_as_de })
  return null
}

/** Two citations pointing at the same passage of the same document. */
export function sameCitation(
  a: Citation | null | undefined,
  b: Citation | null | undefined,
): boolean {
  if (!a || !b) return false
  return a.document_key === b.document_key && a.page === b.page && a.snippet === b.snippet
}

/** A source passage, and the entries it is the citation for. */
export interface SourceGroup {
  citation: Citation
  /** Human labels of the entries this passage is the citation for. */
  covers: string[]
}

/**
 * Fold entries onto their distinct citations, keeping the order they appear in.
 *
 * A BZO zone table row is normally one passage covering six parameters, so
 * showing six identical snippets would bury the one thing the reader has to
 * read. Folding them also makes the shared citation visible AS shared: "this
 * one passage is the evidence for all of these".
 */
export function groupCitations(
  entries: readonly { label: string; citation: Citation | null }[],
): SourceGroup[] {
  const groups: SourceGroup[] = []
  for (const entry of entries) {
    if (!entry.citation) continue
    const c = entry.citation
    const found = groups.find((group) => sameCitation(group.citation, c))
    if (found) found.covers.push(entry.label)
    else groups.push({ citation: entry.citation, covers: [entry.label] })
  }
  return groups
}

/**
 * Parse what a human typed into a parameter's value field.
 *
 * An empty field is `null` — "not read" — and never `0`. A field that looks
 * like a number becomes one so the engine gets a number; everything else stays
 * a string, because a BZO parameter is quite legitimately `W2.2` or `3.5 m`.
 */
export function parseValue(raw: string): string | number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const normalised = trimmed.replace(/'/g, '')
  if (/^-?\d+(\.\d+)?$/.test(normalised)) return Number(normalised)
  return trimmed
}
